import { useState } from 'react';

import CssBaseline from "@mui/material/CssBaseline"

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Settings from '@mui/icons-material/Settings';
import Report from '@mui/icons-material/ListAlt';
import Insights from '@mui/icons-material/Insights';
import Group from '@mui/icons-material/Group';
import Stats from '@mui/icons-material/Insights';
import { SystemBacktestingTable } from './backtesting/SystemBacktestingTable';
import { CustomBacktesting } from './customBacktesting/CustomBacktesting';
import { ApplicationState } from '../interface/ApplicationState';
import { useSelector } from 'react-redux';
import { UserBacktestingStatsTab } from './customBacktesting/UserBacktestingStatsTab';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { dashboardMutedTextSx, getDashboardPanelSx } from './dashboardv2/dashboardStyles';

export const Backtesting = () =>
{
  const theme = useTheme()
  const isAdmin = useSelector((state: ApplicationState) => state.userStore.user.isAdmin)
  const [ tab, setTab ] = useState(0)

  const tabMeta = [
    { value: 0, label: "My custom tests configs", icon: <Settings />, helper: "Manage saved configurations" },
    { value: 1, label: "My custom test results", icon: <Report />, helper: "Review your latest runs" },
    { value: 2, label: "System tests", icon: <Insights />, helper: "Compare system-wide outcomes" },
  ]

  if(isAdmin)
  {
    tabMeta.push({ value: 3, label: "All user configs", icon: <Group />, helper: "Inspect user-created configs" })
    tabMeta.push({ value: 4, label: "User config stats", icon: <Stats />, helper: "Audit queue and schedule health" })
  }

  return(
    <Box
      sx={{
        minHeight: "91vh",
        overflow: "hidden",
        m: { xs: 1.5, md: 2 },
        p: { xs: 2, md: 3 },
        borderRadius: "20px",
        position: "relative",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(180deg, #06111f 0%, #091829 58%, #050c16 100%)`
            : `linear-gradient(180deg, #f3f8ff 0%, #ebf3ff 45%, #ffffff 100%)`,
        border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.55)}`,
      }}
    >
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        background:
          theme.palette.mode === "dark"
            ? `radial-gradient(circle at top left, ${alpha("#38bdf8", 0.16)}, transparent 24%), radial-gradient(circle at top right, ${alpha("#14b8a6", 0.12)}, transparent 28%)`
            : `radial-gradient(circle at top left, ${alpha("#60a5fa", 0.18)}, transparent 24%), radial-gradient(circle at top right, ${alpha("#22c55e", 0.1)}, transparent 30%)`,
      }}
    />

    <CssBaseline />

    <Grid container spacing={3} sx={{ position: "relative", zIndex: 1 }}>
      <Grid item xs={12}>
        <Box
          sx={{
            p: { xs: 2.25, md: 3.5 },
            borderRadius: "16px",
            border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha("#102946", 0.94)} 0%, ${alpha("#081320", 0.96)} 100%)`
                : `linear-gradient(135deg, ${alpha("#ffffff", 0.94)} 0%, ${alpha("#e6f0ff", 0.92)} 100%)`,
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 22px 60px ${alpha("#01050d", 0.45)}`
                : `0 22px 60px ${alpha("#9db3d1", 0.2)}`,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} lg={7}>
              <Box>
                <Typography variant="overline" sx={{ letterSpacing: 2, ...dashboardMutedTextSx(theme) }}>
                  Backtesting Workspace
                </Typography>
                <Typography sx={{ mt: 1, fontSize: { xs: 34, md: 50 }, lineHeight: 0.98, maxWidth: 720 }}>
                  Build, run, and compare strategies from one sharper research surface.
                </Typography>
                <Typography sx={{ mt: 2, maxWidth: 620, fontSize: 16, ...dashboardMutedTextSx(theme) }}>
                  Move between custom configs, result history, and system-wide runs without losing context. Admin views follow the same shell.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4} lg={12}>
                  <Box sx={styles.metricCard(theme)}>
                    <ScienceRoundedIcon fontSize="small" />
                    <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>Configs</Typography>
                    <Typography sx={{ fontSize: 22 }}>
                      Research
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4} lg={12}>
                  <Box sx={styles.metricCard(theme)}>
                    <TimelineRoundedIcon fontSize="small" />
                    <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>Results</Typography>
                    <Typography sx={{ fontSize: 22 }}>
                      Compare runs
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4} lg={12}>
                  <Box sx={styles.metricCard(theme)}>
                    <AutoGraphRoundedIcon fontSize="small" />
                    <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>Mode</Typography>
                    <Typography sx={{ fontSize: 22 }}>
                      {isAdmin ? "Admin enabled" : "Personal workspace"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ ...getDashboardPanelSx(theme), p: { xs: 2, md: 2.5 } }}>
          <Typography variant="overline" sx={{ letterSpacing: 1.6, ...dashboardMutedTextSx(theme) }}>
            Sections
          </Typography>
          <Tabs
            onChange={(_, value) => setTab(value)}
            value={tab}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{
              mt: 1.5,
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": {
                minHeight: 50,
                minWidth: "fit-content",
                alignItems: "flex-start",
                textTransform: "none",
                borderRadius: "12px",
                mr: 1,
                px: 1.75,
                py: 1.1,
                color: alpha(theme.palette.text.primary, 0.68),
                backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.04 : 0.55),
              },
              "& .Mui-selected": {
                color: `${theme.palette.common.white} !important`,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.info.main})`,
              },
            }}
          >
            {tabMeta.map((item) => (
              <Tab
                key={item.value}
                value={item.value}
                iconPosition='start'
                icon={item.icon}
                label={
                  <Box sx={{ textAlign: "left" }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{item.label}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.78 }}>{item.helper}</Typography>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ minHeight: "58vh" }}>
          { tab === 0 && <CustomBacktesting />}
          { tab === 1 && <SystemBacktestingTable ownTests='yes' />}
          { tab === 2 && <SystemBacktestingTable ownTests='no' />}
          { isAdmin && tab === 3 && <CustomBacktesting isAdmin />}
          { isAdmin && tab === 4 && <UserBacktestingStatsTab />}
        </Box>
      </Grid>
    </Grid>

    </Box>
  )
}

const styles = {
  metricCard: (theme: any) => ({
    p: 2,
    borderRadius: "12px",
    display: "grid",
    gap: 0.5,
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.04 : 0.5),
  }),
}
